import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PaperPlaneTilt, Eye, Envelope, ChatTeardropDots, ArrowsClockwise, Warning, CheckCircle, X } from "@phosphor-icons/react";

export default function AdminBroadcasts() {
  const [subscribers, setSubscribers] = useState([]);
  const [marketingTransport, setMarketingTransport] = useState("mock");
  const [loading, setLoading] = useState(true);
  const [excluded, setExcluded] = useState(new Set());
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSampleName, setPreviewSampleName] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const reloadRecipients = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/broadcasts/recipients");
      setSubscribers(r.data.recipients || []);
      setMarketingTransport(r.data.marketing_transport || "mock");
      // Pick a sensible default sample name for the preview (first subscriber with a name)
      const named = (r.data.recipients || []).find(s => s.first_name && s.first_name !== "there");
      if (named) setPreviewSampleName(named.name || named.first_name);
    } finally {
      setLoading(false);
    }
  };

  const reloadHistory = async () => {
    const r = await api.get("/admin/broadcasts/log");
    setHistory(r.data || []);
  };

  useEffect(() => { reloadRecipients(); reloadHistory(); }, []);

  const active = subscribers.filter(s => !excluded.has(s.email));
  const charCount = body.length;
  const overLimit = charCount > 8000;

  const invalidateState = () => {
    setPreviewHtml("");
    setConfirmChecked(false);
    setResult(null);
  };

  const handlePreview = async () => {
    setPreviewError("");
    setPreviewing(true);
    try {
      const r = await api.post("/admin/broadcasts/preview", {
        subject,
        body,
        sample_name: previewSampleName,
      });
      setPreviewHtml(r.data.html);
    } catch (e) {
      setPreviewError(e?.response?.data?.detail || "Preview failed");
      setPreviewHtml("");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendTest = async () => {
    setTestResult(null);
    setTestSending(true);
    try {
      const r = await api.post("/admin/broadcasts/send-test", {
        subject,
        body,
        to: testTo.trim() || undefined,
      });
      setTestResult({ ok: true, to: r.data.to, transport: r.data.transport });
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Send failed" });
    } finally {
      setTestSending(false);
    }
  };

  const canSend = previewHtml && confirmChecked && subject.trim() && body.trim() && !overLimit && active.length > 0 && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    if (!window.confirm(`Send to ${active.length} subscriber${active.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/admin/broadcasts/send", {
        subject,
        body,
        recipient_emails: active.map(s => s.email),
      });
      setResult(r.data);
      reloadHistory();
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Send failed" });
    } finally {
      setSending(false);
    }
  };

  const toggleExclude = (email) => {
    const next = new Set(excluded);
    if (next.has(email)) next.delete(email); else next.add(email);
    setExcluded(next);
    invalidateState();
  };

  return (
    <div className="p-10 md:p-12" data-testid="admin-broadcasts">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-ms-stone">Marketing</p>
          <h1 className="serif text-4xl text-ms-earth mt-2">Broadcasts</h1>
          <p className="mt-2 text-sm text-ms-stone max-w-xl">
            Send a one-off newsletter to your "Stay in the loop" subscriber list. Marketing runs through Resend — separate from your transactional Gmail SMTP so a marketing send can't drag your booking confirmations into spam.
          </p>
        </div>
        <button
          onClick={() => { reloadRecipients(); reloadHistory(); }}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-ms-edge rounded-md hover:bg-ms-cream"
          data-testid="broadcast-refresh"
        >
          <ArrowsClockwise size={14} /> Refresh
        </button>
      </div>

      {marketingTransport === "mock" && (
        <div className="mt-6 p-4 bg-ms-warn/10 border border-ms-warn/30 rounded-xl text-sm text-ms-earth flex items-start gap-3" data-testid="broadcast-mock-warning">
          <Warning size={18} className="text-ms-warn mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Resend not configured — broadcasts will be <b>mock-logged</b> (no real emails sent).</p>
            <p className="text-xs text-ms-stone mt-1">Add <code>RESEND_API_KEY</code> to <code>backend/.env</code> to enable live newsletter sends. Transactional emails (booking confirmations, etc.) continue to send via Gmail SMTP regardless.</p>
          </div>
        </div>
      )}

      {/* Top: subscriber count + compose */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-ms-edge rounded-2xl p-6">
          <p className="label-eyebrow text-ms-stone text-xs">Active subscribers</p>
          <p className="serif text-5xl text-ms-earth mt-3" data-testid="broadcast-subscriber-count">{loading ? "…" : active.length}</p>
          <p className="text-xs text-ms-stone mt-2">
            {excluded.size > 0 && <span>{excluded.size} excluded · </span>}
            {subscribers.length} total on list
          </p>
          <p className="text-xs text-ms-stone mt-1">Marketing channel: <b className="text-ms-earth">{marketingTransport}</b></p>
        </div>

        <div className="lg:col-span-2 bg-white border border-ms-edge rounded-2xl p-6 space-y-4">
          <label className="block">
            <span className="label-eyebrow text-ms-stone text-xs">Subject</span>
            <input
              value={subject}
              onChange={e => { setSubject(e.target.value); invalidateState(); }}
              placeholder="A new mystery just dropped"
              maxLength={140}
              className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-ms-brass"
              data-testid="broadcast-subject"
            />
          </label>
          <label className="block">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow text-ms-stone text-xs">Body</span>
              <span className="text-xs text-ms-stone">
                Use <code className="bg-ms-cream px-1 rounded">**bold**</code>, <code className="bg-ms-cream px-1 rounded">[link](https://...)</code>, blank lines for paragraphs, <code className="bg-ms-cream px-1 rounded">{"{first_name}"}</code> for the recipient's name.
              </span>
            </div>
            <textarea
              value={body}
              onChange={e => { setBody(e.target.value); invalidateState(); }}
              rows={10}
              placeholder={"Hi {first_name},\n\n**A new mystery just dropped.**\n\nOur newest tour is now bookable — [grab your dates here](https://mysteryserved.com/tours).\n\nSee you on the street,\nMystery Served"}
              className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-ms-brass"
              data-testid="broadcast-body"
            />
            <p className={`mt-1 text-xs ${overLimit ? "text-ms-danger" : "text-ms-stone"}`}>{charCount} chars</p>
          </label>

          <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-ms-edge">
            <label className="block flex-1 min-w-[200px]">
              <span className="label-eyebrow text-ms-stone text-[10px]">Sample name for preview</span>
              <input
                value={previewSampleName}
                onChange={e => setPreviewSampleName(e.target.value)}
                placeholder="Alice"
                className="mt-1.5 w-full border border-ms-edge rounded-md px-3 py-2 text-sm"
                data-testid="broadcast-sample-name"
              />
            </label>
            <button
              onClick={handlePreview}
              disabled={!subject.trim() || !body.trim() || previewing}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-ms-earth text-ms-earth rounded-md hover:bg-ms-earth hover:text-ms-cream transition-colors disabled:opacity-40"
              data-testid="broadcast-preview-btn"
            >
              <Eye size={14} /> {previewing ? "Rendering…" : "Preview"}
            </button>
          </div>
          {previewError && <p className="text-xs text-ms-danger" data-testid="broadcast-preview-error">{previewError}</p>}
        </div>
      </div>

      {/* Preview pane */}
      {previewHtml && (
        <section className="mt-8 bg-white border border-ms-edge rounded-2xl overflow-hidden" data-testid="broadcast-preview-pane">
          <div className="flex items-center justify-between p-4 border-b border-ms-edge bg-ms-cream/40">
            <p className="label-eyebrow text-ms-stone text-xs">Exact email preview · personalized for "{previewSampleName || "there"}"</p>
            <div className="flex items-center gap-2">
              <input
                value={testTo}
                onChange={e => setTestTo(e.target.value)}
                placeholder="test@yourdomain.com (defaults to admin)"
                className="px-3 py-1.5 text-xs border border-ms-edge rounded-md min-w-[260px]"
                data-testid="broadcast-test-to"
              />
              <button
                onClick={handleSendTest}
                disabled={testSending || !subject.trim() || !body.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-ms-edge rounded-md hover:bg-white disabled:opacity-40"
                data-testid="broadcast-send-test-btn"
              >
                <PaperPlaneTilt size={12} /> {testSending ? "Sending…" : "Send test to me"}
              </button>
            </div>
          </div>
          {testResult && (
            <div className={`px-4 py-2 text-xs border-b border-ms-edge ${testResult.ok ? "bg-ms-success/10 text-ms-success" : "bg-ms-danger/10 text-ms-danger"}`} data-testid="broadcast-test-result">
              {testResult.ok ? (
                <span><CheckCircle size={12} weight="fill" className="inline mr-1" /> Test sent to <b>{testResult.to}</b> via <b>{testResult.transport}</b> — check your inbox.</span>
              ) : (
                <span><Warning size={12} weight="fill" className="inline mr-1" /> {testResult.error}</span>
              )}
            </div>
          )}
          <iframe
            srcDoc={previewHtml}
            title="Broadcast preview"
            className="w-full h-[600px] bg-ms-cream"
            data-testid="broadcast-preview-iframe"
          />
        </section>
      )}

      {/* Recipients */}
      <section className="mt-8 bg-white border border-ms-edge rounded-2xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="label-eyebrow text-ms-stone text-xs">Recipients</p>
            <p className="text-sm text-ms-earth mt-1">
              Will send to <b data-testid="broadcast-active-count">{active.length}</b> of {subscribers.length} subscriber{subscribers.length === 1 ? "" : "s"}.
              {excluded.size > 0 && <span className="text-ms-warn"> {excluded.size} excluded.</span>}
            </p>
          </div>
          {excluded.size > 0 && (
            <button onClick={() => { setExcluded(new Set()); invalidateState(); }} className="text-xs text-ms-brass label-eyebrow" data-testid="broadcast-restore-all">
              Restore all
            </button>
          )}
        </div>
        {loading ? (
          <p className="mt-4 text-sm text-ms-stone">Loading subscribers…</p>
        ) : subscribers.length === 0 ? (
          <p className="mt-4 text-sm text-ms-stone" data-testid="broadcast-no-subscribers">
            No active subscribers yet. Your footer signup form is the way people join the list.
          </p>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2 max-h-64 overflow-y-auto" data-testid="broadcast-recipient-chips">
            {subscribers.map(s => {
              const ex = excluded.has(s.email);
              return (
                <button
                  key={s.email}
                  onClick={() => toggleExclude(s.email)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${
                    ex ? "border-ms-edge text-ms-stone line-through opacity-60" : "border-ms-edge text-ms-earth bg-white hover:border-ms-earth/40"
                  }`}
                  data-testid={`broadcast-recipient-${s.email}`}
                  title={ex ? "Click to include" : "Click to exclude"}
                >
                  <Envelope size={10} />
                  <span>{s.email}</span>
                  {s.name && <span className="text-ms-stone">· {s.name}</span>}
                  {!ex && <X size={10} />}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Send block */}
      <section className="mt-8 bg-white border-2 border-ms-earth rounded-2xl p-6" data-testid="broadcast-send-block">
        <p className="label-eyebrow text-ms-stone text-xs">Final step</p>
        <h2 className="serif text-xl text-ms-earth mt-2">Send broadcast</h2>

        {!previewHtml ? (
          <p className="mt-3 text-sm text-ms-stone">Render a preview above before you can send.</p>
        ) : (
          <>
            <label className="mt-4 flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={e => setConfirmChecked(e.target.checked)}
                className="mt-1"
                data-testid="broadcast-confirm-checkbox"
              />
              <span className="text-sm text-ms-earth">
                I've reviewed the preview and want to send this broadcast to <b>{active.length}</b> active subscriber{active.length === 1 ? "" : "s"}.
              </span>
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={handleSend}
                disabled={!canSend}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-ms-earth text-ms-cream rounded-md label-eyebrow text-xs disabled:opacity-40 hover:bg-ms-brass transition-colors"
                data-testid="broadcast-send-btn"
              >
                <PaperPlaneTilt size={14} /> {sending ? "Sending…" : `Send to ${active.length}`}
              </button>
            </div>
          </>
        )}

        {result && (
          <div className={`mt-5 p-4 rounded-xl border ${result.error ? "border-ms-danger bg-ms-danger/5 text-ms-danger" : "border-ms-success bg-ms-success/5 text-ms-earth"}`} data-testid="broadcast-result">
            {result.error ? (
              <p><b>Failed:</b> {result.error}</p>
            ) : (
              <div className="text-sm">
                <p>
                  <CheckCircle size={14} weight="fill" className="inline mr-1 text-ms-success" />
                  <b>Sent {result.sent_count}</b>
                  {result.failed_count > 0 && <span className="text-ms-warn"> · {result.failed_count} failed</span>}
                  {result.transport === "mock" && <span className="text-ms-stone"> (mock-logged)</span>}
                </p>
                {result.failed_count > 0 && (
                  <ul className="mt-2 text-xs text-ms-warn list-disc list-inside">
                    {result.failed.map((f, i) => <li key={i}>{f.email}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {/* History */}
      <section className="mt-8 bg-white border border-ms-edge rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-ms-edge">
          <p className="label-eyebrow text-ms-stone text-xs">Sent broadcasts</p>
          <h2 className="serif text-xl text-ms-earth mt-1">History</h2>
        </div>
        {history.length === 0 ? (
          <p className="p-6 text-sm text-ms-stone" data-testid="broadcast-history-empty">No broadcasts sent yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-ms-cream/40 border-b border-ms-edge">
              <tr>
                <th className="text-left p-4 label-eyebrow text-[10px]">Sent at</th>
                <th className="text-left p-4 label-eyebrow text-[10px]">Subject</th>
                <th className="text-left p-4 label-eyebrow text-[10px]">Sent</th>
                <th className="text-left p-4 label-eyebrow text-[10px]">Failed</th>
                <th className="text-left p-4 label-eyebrow text-[10px]">Transport</th>
              </tr>
            </thead>
            <tbody>
              {history.map(h => (
                <tr key={h.id} className="border-b border-ms-edge last:border-0" data-testid={`broadcast-history-${h.id}`}>
                  <td className="p-4 text-ms-stone">{new Date(h.created_at).toLocaleString()}</td>
                  <td className="p-4 text-ms-earth">{h.subject}</td>
                  <td className="p-4 text-ms-success">{h.sent_count}</td>
                  <td className={`p-4 ${h.failed_count ? "text-ms-warn" : "text-ms-stone"}`}>{h.failed_count}</td>
                  <td className="p-4 text-ms-stone">{h.transport}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
